import { Modal, Input } from "antd";
import { Button as MuiButton } from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import {
  InstitutionSendPayment,
  MasaVSendPayments,
  SendPaymentsRecord,
} from "masav";
import moment from "moment";
import { masavFileApiService, savedTablesApiService } from "./apiService";
import { buildTermsUrl } from "../components/TermsLink";

const DownloadMasavFileModalContent = ({
  institution,
  transactions,
  t,
  onConfirm,
  onClose,
  onSaveAndDownload,
}) => {
  const termsHref =
    typeof window !== "undefined"
      ? buildTermsUrl(
          `${window.location.pathname}${window.location.search || ""}`
        )
      : "/terms";

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          padding: "12px 14px",
          background: "#fff7f2",
          borderRadius: 8,
          borderInlineStart: "4px solid #FF6B35",
        }}
      >
        <p
          style={{
            margin: "0 0 8px 0",
            fontSize: 13,
            lineHeight: 1.55,
            color: "#262626",
          }}
        >
          {t("download-summary-disclaimer-line1-before")}
          <a
            href={termsHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#FF6B35",
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {t("translation:terms-page-link")}
          </a>
          {t("download-summary-disclaimer-line1-after")}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.55,
            color: "#262626",
          }}
        >
          {t("download-summary-disclaimer-line2")}
        </p>
      </div>
      <div>
        <b>{t("institution-id-label")}</b>: {institution.institutionId}
      </div>
      <div>
        <b>{t("sending-institution-id-label")}</b>:{" "}
        {institution.sendingInstitutionId}
      </div>
      <div>
        <b>{t("institution-name-label")}</b>: {institution.institutionName}
      </div>
      <div>
        <b>{t("file-name")}</b>: {`msv.${institution.serialNumber}`}
      </div>
      <div>
        <b>{t("number-of-transactions")}</b>: {transactions.length}
      </div>
      <div>
        <b>{t("amount")}</b>:{" "}
        {new Intl.NumberFormat("il-IL", {
          style: "currency",
          currency: "ILS",
        }).format(
          transactions.map((trans) => trans.amount).reduce((a, b) => a + b, 0)
        )}
      </div>
      <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "16px" }}>
        <MuiButton 
          onClick={onClose}
          variant="outlined"
          size="large"
          sx={{ 
            borderRadius: "8px",
            fontWeight: "600",
            height: "40px",
            padding: "0 20px",
            fontSize: "14px",
            textTransform: "none"
          }}
        >
          {t("translation:cancel")}
        </MuiButton>
        <MuiButton
          variant="outlined"
          size="large"
          onClick={onConfirm}
          sx={{ 
            borderRadius: "8px",
            fontWeight: "600",
            height: "40px",
            padding: "0 20px",
            fontSize: "14px",
            textTransform: "none",
            borderColor: "#FF6B35",
            color: "#FF6B35",
            "&:hover": {
              borderColor: "#E55A2B",
              color: "#E55A2B",
              backgroundColor: "rgba(255, 107, 53, 0.04)"
            }
          }}
        >
          {t("download-only")}
        </MuiButton>
        <MuiButton
          variant="contained"
          color="warning"
          size="large"
          onClick={onSaveAndDownload}
          sx={{ 
            borderRadius: "8px",
            fontWeight: "600",
            height: "40px",
            padding: "0 20px",
            fontSize: "14px",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          {t("download-and-save")}
        </MuiButton>
      </div>
    </div>
  );
};

const showTableNameInput = (t) =>
  new Promise((resolve) => {
    let tableName = "";
    const manageModal = Modal.confirm({
      className: "hide-modal-footer",
      title: t("save-table-name-label"),
      icon: null,
      bodyStyle: { direction: t("translation:direction") },
      content: (
        <div>
          <Input
            placeholder={t("save-table-name-placeholder")}
            onChange={(e) => {
              tableName = e.target.value;
            }}
            onPressEnter={() => {
              if (tableName.trim()) {
                manageModal.destroy();
                resolve(tableName.trim());
              }
            }}
            autoFocus
            style={{ width: "100%" }}
          />
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "16px" }}>
            <MuiButton 
              onClick={() => {
                manageModal.destroy();
                resolve(null);
              }}
              variant="outlined"
              size="large"
              sx={{ 
                borderRadius: "8px",
                fontWeight: "600",
                height: "40px",
                padding: "0 20px",
                fontSize: "14px",
                textTransform: "none"
              }}
            >
              {t("translation:cancel")}
            </MuiButton>
            <MuiButton
              variant="contained"
              color="warning"
              size="large"
              onClick={() => {
                if (tableName.trim()) {
                  manageModal.destroy();
                  resolve(tableName.trim());
                }
              }}
              sx={{ 
                borderRadius: "8px",
                fontWeight: "600",
                height: "40px",
                padding: "0 20px",
                fontSize: "14px",
                textTransform: "none"
              }}
            >
              {t("translation:ok")}
            </MuiButton>
          </div>
        </div>
      ),
    });
  });

const showSummery = (institution, transactions, t) =>
  new Promise((resolve) => {
    const manageModal = Modal.confirm({
      className: "hide-modal-footer",
      title: t("masav-file-summery"),
      icon: null,
      bodyStyle: { direction: t("translation:direction") },
      content: (
        <DownloadMasavFileModalContent
          transactions={transactions}
          institution={institution}
          t={t}
          onClose={() => {
            manageModal.destroy();
            resolve({ action: "cancel" });
          }}
          onConfirm={() => {
            manageModal.destroy();
            resolve({ action: "download", shouldSave: false });
          }}
          onSaveAndDownload={async () => {
            manageModal.destroy();
            // Show second modal for table name
            const tableName = await showTableNameInput(t);
            if (tableName) {
              resolve({ action: "download", shouldSave: true, tableName });
            } else {
              resolve({ action: "cancel" });
            }
          }}
        />
      ),
    });
  });

export const generateMasavFile = async ({ institution, transactions }, t, onTableSaved) => {
  const tt = (str) => t(`translation:${str}`);
  const messages = [];

  if (!institution.institutionId.match(/^\d{8}$/))
    messages.push(tt("invalid-institution-id"));
  if (!institution.institutionName.match(/^.{1,30}$/))
    messages.push(tt("invalid-institution-Name"));
  if (!institution.sendingInstitutionId.match(/^\d{5}$/))
    messages.push(tt("invalid-institution-sending-id"));
  if (!institution.serialNumber.match(/^\d{3}$/))
    messages.push(tt("invalid-serial-number"));
  if (!transactions.length) messages.push(tt("no-transactions"));

  transactions.forEach(
    ({ accountId, amount, bankId, branchId, payeeId, payeeName }, i) => {
      if (!accountId.match(/^\d{1,9}$/))
        messages.push(
          `${tt("in-row-number-")}${i + 1}${tt("-bank-account-is-not-valid")}`
        );
      if (isNaN(amount) || amount <= 0)
        messages.push(
          `${tt("in-row-number-")}${i + 1}${tt("-amount-is-not-valid")}`
        );
      if (!bankId.match(/^\d{1,2}$/))
        messages.push(
          `${tt("in-row-number-")}${i + 1}${tt("-bank-id-is-not-valid")}`
        );
      if (!branchId.match(/^\d{1,3}$/))
        messages.push(
          `${tt("in-row-number-")}${i + 1}${tt("-branch-id-is-not-valid")}`
        );
      if (!payeeId.match(/^\d{1,9}$/))
        messages.push(
          `${tt("in-row-number-")}${i + 1}${tt("-payee-id-is-not-valid")}`
        );
      if (!payeeName.match(/^.{0,16}$/))
        messages.push(
          `${tt("in-row-number-")}${i + 1}${tt("-payee-name-is-not-valid")}`
        );
    }
  );

  if (messages.length) {
    const errorModal = Modal.error({
      bodyStyle: { direction: t("translation:direction") },
      okText: null,
      okButtonProps: { style: { display: 'none' } },
      closable: true,
      closeIcon: <CloseOutlined style={{ fontSize: "18px", color: "#8c8c8c" }} />,
      footer: (
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
          <MuiButton
            onClick={() => errorModal.destroy()}
            variant="contained"
            size="large"
            sx={{ 
              borderRadius: "8px",
              fontWeight: "600",
              height: "40px",
              padding: "0 20px",
              fontSize: "14px",
              textTransform: "none",
              backgroundColor: "#FF6B35",
              "&:hover": {
                backgroundColor: "#E55A2B"
              }
            }}
          >
            {t("translation:ok")}
          </MuiButton>
        </div>
      ),
      title: tt("masav-file-errors"),
      content: (
        <ul>
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      ),
    });
    return;
  }

  const result = await showSummery(institution, transactions, t);
  if (!result || result.action === "cancel") return;
  
  const shouldSave = result.shouldSave === true;
  const tableName = result.tableName;

  // Check download availability before proceeding - token is sent in headers via createAuthHeaders
  // Send transactions so Lambda knows to update the download count
  try {
    const canDownload = await masavFileApiService.generateMasavFile(transactions);
    if (!canDownload) {
      const manageModal = Modal.error({
        className: "hide-modal-footer",
        bodyStyle: { direction: t("translation:direction") },
        title: tt("download-limit-reached-title"),
        icon: null,
        content: (
          <div>
            <div style={{ marginBottom: "20px" }}>
              {tt("download-limit-reached-message")}
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <MuiButton
                onClick={() => {
                  manageModal.destroy();
                }}
                variant="outlined"
                size="large"
                sx={{ 
                  borderRadius: "8px",
                  fontWeight: "600",
                  height: "40px",
                  padding: "0 20px",
                  fontSize: "14px",
                  textTransform: "none",
                  borderColor: "#FF6B35",
                  color: "#FF6B35",
                  "&:hover": {
                    borderColor: "#E55A2B",
                    color: "#E55A2B",
                    backgroundColor: "rgba(255, 107, 53, 0.04)"
                  }
                }}
              >
                {t("translation:close")}
              </MuiButton>
            </div>
          </div>
        ),
      });
      return;
    }
  } catch (error) {
    console.error("Failed to check download availability:", error);
    // On error, show the error message and don't proceed
    const manageModal = Modal.error({
      className: "hide-modal-footer",
      bodyStyle: { direction: t("translation:direction") },
      title: tt("download-limit-reached-title"),
      icon: null,
      content: (
        <div>
          <div style={{ marginBottom: "20px" }}>
            {tt("download-limit-reached-message")}
          </div>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <MuiButton
              onClick={() => {
                manageModal.destroy();
              }}
              variant="outlined"
              size="large"
              sx={{ 
                borderRadius: "8px",
                fontWeight: "600",
                height: "40px",
                padding: "0 20px",
                fontSize: "14px",
                textTransform: "none",
                borderColor: "#FF6B35",
                color: "#FF6B35",
                "&:hover": {
                  borderColor: "#E55A2B",
                  color: "#E55A2B",
                  backgroundColor: "rgba(255, 107, 53, 0.04)"
                }
              }}
            >
              {t("translation:close")}
            </MuiButton>
          </div>
        </div>
      ),
    });
    return;
  }

  const masavFile = new MasaVSendPayments();
  const masav_institution = new InstitutionSendPayment(
    institution.institutionId || "",
    institution.sendingInstitutionId || "",
    moment().format("YYMMDD"),
    moment().format("YYMMDD"),
    institution.institutionName || "",
    institution.serialNumber || ""
  );

  transactions.forEach(
    ({
      accountId,
      amount,
      bankId,
      branchId,
      payeeId,
      payeeName,
      payeeNumber,
    }) => {
      masav_institution.addPaymentRecord(
        new SendPaymentsRecord(
          bankId || "",
          branchId || "",
          accountId || "",
          payeeId || "",
          payeeName || "",
          payeeNumber || "",
          amount || 0
        )
      );
    }
  );

  masavFile.addInstitution(masav_institution);

  // without zip file
  const blob = new Blob([masavFile.toBuffer()], {
    type: "application/octet-stream",
  });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `msv.${institution.serialNumber}`;
  link.click();

  // Save table if requested
  if (shouldSave && tableName) {
    try {
      await savedTablesApiService.saveTable(tableName, institution, transactions);
      // Refresh saved tables list if callback provided
      if (onTableSaved) {
        try {
          const updatedTables = await savedTablesApiService.getSavedTables();
          onTableSaved(updatedTables);
        } catch (error) {
          console.error("Failed to refresh saved tables:", error);
        }
      }
    } catch (error) {
      console.error("Failed to save table:", error);
      // Don't block the download if save fails
    }
  }

  // Update download count after successful download
  try {
    await masavFileApiService.getUsageInfo();
    // Also call window.refreshMasavUsage if it exists
    if (typeof window.refreshMasavUsage === "function") {
      await window.refreshMasavUsage();
    }
  } catch (error) {
    console.error("Failed to refresh usage info after download:", error);
    // Don't block the download if this fails
  }
};
